import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthModal from '../../components/AuthModal/AuthModal';
import { EmptyShareState, PublicPageCTA, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import AuthGateAction from '../../components/PublicShare/AuthGateAction';
import { CoinPreview } from '../../components/PublicShare/PreviewCards';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { PublicShareService } from '../../services/publicShareService';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { buildOgImageUrl, useShareMetadata } from '../../utils/shareMetadata';
import { seasonalEditionLabel, withSeasonLabel } from '@aluvo/constants/src/collectionAvailability';
import styles from '../../components/PublicShare/PublicShare.module.css';

export default function PublicAchievementShare() {
    const { achievementId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        PublicShareService.getAchievement(achievementId)
            .then((data) => mounted && setPayload(data))
            .catch((err) => mounted && setError(err.message || 'This achievement is no longer available.'));
        return () => { mounted = false; };
    }, [achievementId]);

    const achievement = payload?.achievement;
    const share = useMemo(() => {
        if (!achievement) return { title: 'Aluvo Achievement | Aluvo', description: 'A verified achievement on Aluvo.' };
        return {
            url: `${window.location.origin}/achievement/${achievement.id}`,
            title: `${achievement.title.replace(/\.$/, '')} | Aluvo`,
            description: achievement.explanation,
            text: withSeasonLabel(`${achievement.title.replace(/\.$/, '')} on Aluvo.`, achievement.collection || achievement.coin?.collection),
            tier: achievement.coin?.tier || achievement.collection?.reward?.tier,
            image: achievement.coin?.coinImageUrl || achievement.collection?.reward?.coinImageUrl || buildOgImageUrl({ title: achievement.title, subtitle: achievement.explanation, username: achievement.user?.username, tier: achievement.coin?.tier }),
        };
    }, [achievement]);

    useShareMetadata(share);

    if (error) return <EmptyShareState icon="🏆" title="This achievement is no longer available." message="The shared achievement may have been removed or is no longer public." />;
    if (!payload) return <EmptyShareState icon="🏆" title="Loading achievement" message="Preparing this Aluvo milestone..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This collector's profile is private." message="This shared achievement is not public right now." />;

    const isLegendary = achievement.achievementType === 'legendary_unlocked' || achievement.coin?.tier === 'legendary';
    const quizPath = achievement.coin?.media ? `/quiz/${achievement.coin.media.kind}-${achievement.coin.media.tmdbId}` : '/discover';
    const collectionId = achievement.collection?.slug || achievement.collection?.id;
    const edition = seasonalEditionLabel(achievement.collection || achievement.coin?.collection);

    return (
        <PublicShareLayout theme={isLegendary ? 'legendary' : 'cinematic'} eyebrow="Achievement Proof">
            <section className={styles.heroCard}>
                <div>
                    <div className={styles.profileIdentity}>
                        {achievement.user?.avatarUrl ? <img className={styles.avatar} src={achievement.user.avatarUrl} alt={achievement.user.username} /> : <div className={styles.avatar}>🏆</div>}
                        <div>
                            <span className={styles.statusPill}>{labelForType(achievement.achievementType)}</span>
                            {edition && <span className={styles.pill} data-testid="season-edition">{edition}</span>}
                            <h1 className={styles.title}>{achievement.title}</h1>
                        </div>
                    </div>
                    <p className={styles.subtitle}>{achievement.explanation}</p>
                    <div className={styles.metaRow}>
                        <Meta label="Achieved" value={formatDate(achievement.date)} />
                        <Meta label="Collector" value={`@${achievement.user?.username}`} />
                        <Meta label="Related" value={achievement.relatedTitle || 'Aluvo'} />
                    </div>
                    <div className={styles.heroActions}>
                        <ShareMenu {...share} compact />
                        <Link className={styles.secondaryAction} to={`/u/${achievement.user?.username}`}>View Profile</Link>
                        {achievement.coin?.media && <AuthGateAction className={styles.primaryAction} gateAction={gateAction} authMessage={AUTH_MESSAGES.QUIZ} onClick={() => navigate(quizPath)}>Try This Quiz</AuthGateAction>}
                        {collectionId && <Link className={styles.ghostAction} to={`/collection/${collectionId}?u=${achievement.user?.username}`}>View Collection</Link>}
                    </div>
                </div>
                <div className={styles.panel}>
                    {achievement.coin ? <CoinPreview coin={achievement.coin} size="lg" legendary={isLegendary} /> : <CoinPreview coin={{ tier: achievement.collection?.reward?.tier || 'legendary', coinImageUrl: achievement.collection?.reward?.coinImageUrl }} size="lg" legendary />}
                </div>
            </section>
            <AppPromotionCard variant="share_page" compact />
            <PublicPageCTA title="Start earning your own Aluvo coins." text="Verify what you watch, unlock achievements, and share your public trophy case." buttonText="Create your profile" />
            <AuthModal {...authModalProps} />
        </PublicShareLayout>
    );
}

function labelForType(type) {
    return String(type || 'achievement').replace(/_/g, ' ');
}
function Meta({ label, value }) {
    return <div className={styles.stat}><span className={styles.metaLabel}>{label}</span><strong style={{ fontSize: '1rem' }}>{value}</strong></div>;
}
function formatDate(date) {
    if (!date) return 'Recently';
    return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(date));
}